import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { displayPhone, formatPhoneUS, digitsOnly } from "../lib/format";
import { ArrowLeft, EnvelopeSimple, Phone, Folders, PencilSimple, Trash, UserSwitch } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { useImpersonation } from "../context/ImpersonationContext";

const STATUS_TONE = {
    new: "bg-slate-100 text-slate-700",
    investigating: "bg-blue-50 text-blue-700",
    disputing: "bg-amber-50 text-amber-700",
    negotiating: "bg-violet-50 text-violet-700",
    settled: "bg-emerald-50 text-emerald-700",
    closed: "bg-slate-100 text-slate-500",
};

const EMPTY = { name: "", email: "", phone: "", notes: "" };

export default function ClientDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const { start: startImpersonation } = useImpersonation();

    const [client, setClient] = useState(null);
    const [loading, setLoading] = useState(true);

    const [editOpen, setEditOpen] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    const [confirmDel, setConfirmDel] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const load = () => {
        api.get(`/clients/${id}`).then((r) => setClient(r.data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, [id]);

    const openEdit = () => {
        if (!client) return;
        setForm({
            name: client.name || "",
            email: client.email || "",
            phone: client.phone || "",
            notes: client.notes || "",
        });
        setEditOpen(true);
    };

    const saveEdit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.patch(`/clients/${id}`, form);
            toast.success("Client updated");
            setEditOpen(false);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const doDelete = async () => {
        setDeleting(true);
        try {
            await api.delete(`/clients/${id}`);
            toast.success("Client deleted");
            nav("/clients");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setDeleting(false); }
    };

    const doImpersonate = () => {
        if (!client) return;
        startImpersonation(client);
        toast.success(`Now focused on ${client.name}`);
        nav("/dashboard");
    };

    if (loading) return <p className="text-slate-500">Loading…</p>;
    if (!client) return <p className="text-slate-500">Client not found.</p>;

    return (
        <div className="space-y-8" data-testid="client-detail">
            <Link to="/clients" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
                <ArrowLeft size={14} /> Back to clients
            </Link>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                <div className="lg:col-span-2">
                    <p className="overline">Client profile</p>
                    <h1 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight">{client.name}</h1>
                    <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-600">
                        {client.email && <span className="inline-flex items-center gap-1.5"><EnvelopeSimple size={14} /> {client.email}</span>}
                        {client.phone && <span className="inline-flex items-center gap-1.5"><Phone size={14} /> {displayPhone(client.phone)}</span>}
                    </div>
                    {client.notes && <p className="mt-5 text-slate-700 leading-relaxed max-w-2xl">{client.notes}</p>}

                    <div className="mt-6 flex flex-wrap gap-2" data-testid="client-detail-actions">
                        <Button
                            type="button"
                            onClick={openEdit}
                            data-testid="client-detail-edit-btn"
                            className="h-9 bg-[#0F1E3D] hover:bg-[#1A2C57] text-white"
                        >
                            <PencilSimple size={14} className="mr-1.5" /> Edit
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={doImpersonate}
                            data-testid="client-detail-impersonate-btn"
                            className="h-9 border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                        >
                            <UserSwitch size={14} className="mr-1.5" /> Focus on this client
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setConfirmDel(true)}
                            data-testid="client-detail-delete-btn"
                            className="h-9 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                        >
                            <Trash size={14} className="mr-1.5" /> Delete
                        </Button>
                    </div>
                </div>
                <div className="bg-white border border-slate-200 rounded-md p-6 flex flex-col items-start gap-2">
                    <Folders size={18} weight="duotone" className="text-[#2563EB]" />
                    <p className="overline">Cases on file</p>
                    <p className="font-serif text-4xl">{client.cases?.length || 0}</p>
                </div>
            </div>

            <div>
                <h2 className="font-serif text-2xl mb-4">Cases</h2>
                {(!client.cases || client.cases.length === 0) ? (
                    <p className="text-slate-500 text-sm">No cases linked to this client yet. Create one from the New Case page.</p>
                ) : (
                    <div className="border border-slate-200 rounded-md overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-slate-500">
                                <tr>
                                    <th className="text-left px-4 py-3 font-medium">Title</th>
                                    <th className="text-left px-4 py-3 font-medium">Creditor</th>
                                    <th className="text-left px-4 py-3 font-medium">Status</th>
                                    <th className="text-right px-4 py-3 font-medium">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {client.cases.map((c) => (
                                    <tr key={c.id} className="border-t border-slate-200 hover:bg-slate-50">
                                        <td className="px-4 py-3">
                                            <Link to={`/cases/${c.id}`} className="font-medium text-slate-900 hover:text-[#2563EB]">{c.title}</Link>
                                        </td>
                                        <td className="px-4 py-3 text-slate-600">{c.creditor_name}</td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_TONE[c.status] || STATUS_TONE.new}`}>
                                                {c.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right font-mono">${Number(c.amount_owed).toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl">Edit client</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={saveEdit} className="space-y-3" data-testid="client-detail-edit-form">
                        <div>
                            <Label htmlFor="cn">Name *</Label>
                            <Input id="cn" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11" />
                        </div>
                        <div>
                            <Label htmlFor="ce">Email</Label>
                            <Input id="ce" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-11" />
                        </div>
                        <div>
                            <Label htmlFor="cp">Phone</Label>
                            <Input
                                id="cp"
                                inputMode="tel"
                                placeholder="(555) 123-4567"
                                value={formatPhoneUS(form.phone)}
                                onChange={(e) => setForm({ ...form, phone: digitsOnly(e.target.value) })}
                                className="mt-1.5 h-11"
                            />
                        </div>
                        <div>
                            <Label htmlFor="cnt">Notes</Label>
                            <Textarea id="cnt" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" />
                        </div>
                        <DialogFooter>
                            <Button type="submit" disabled={saving} data-testid="client-detail-edit-save" className="bg-[#0F1E3D] hover:bg-[#1A2C57]">
                                {saving ? "Saving…" : "Save changes"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
                <AlertDialogContent data-testid="client-detail-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-serif text-2xl">Delete this client?</AlertDialogTitle>
                        <AlertDialogDescription>
                            You&apos;re about to permanently delete <strong>{client.name}</strong>.
                            {client.cases?.length > 0 && (
                                <span className="block mt-2 text-amber-700">
                                    {client.cases.length} linked case{client.cases.length === 1 ? "" : "s"} will be kept but unlinked from this client.
                                </span>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); doDelete(); }}
                            disabled={deleting}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="client-detail-delete-confirm"
                        >
                            {deleting ? "Deleting…" : "Delete client"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
